import ClassTeachingDetails from "@/components/ClassTeachingDetails";
/**
 * @fileoverview Home / Dashboard ("Studio Quiet"): today's schedule, a quiet
 * stat row (with a "+ Add student" pill shortcut on the Students tile), Recent
 * students + Recent lessons columns, saved lesson plans, and quick tools.
 * Recording lives in the sidebar; student rows offer a quiet hover mic
 * shortcut (never a persistent yellow button).
 */

import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ProgramChip from "@/components/ProgramChip";
import { AuthContext } from "@/App";
import api from "@/lib/api";
import { uploadQueue } from "@/lib/uploadQueue";
import { savedCountLabel } from "@/lib/savedRecordings";
import { programClassPath } from "@/lib/programsRoute";
import { SCHEDULING_ENABLED, SCHEDULER_VIEW_ENABLED } from "@/lib/featureFlags";
import { occurrenceKey, teachingNext, teachingToday } from "@/lib/schedulingView";
import { formatTime12h, isoDate, recordHrefForOccurrence, shortDateLabel } from "@/lib/schedule";
import { anyStillAhead, inBusiness, nextHeading, nextSession, tomorrowWord } from "@/lib/nextTeaching";
import { formatLessonDuration } from "@/lib/duration";
import { lessonDate, recordedAt, sortChronologically } from "@/lib/lessonHistory";
import { firstName } from "@/lib/userName";
import { accountFeatureEnabled } from "@/lib/orgFeatures";
import { ConsentBadge } from "@/components/ConsentBadge";
import { isProgramRosterStudent } from "@/lib/importedStudents";
import { Card, StatCard, ListRow, Avatar, SectionHeader, Eyebrow, Icon, StatusPill, Tag } from "@/components/studio/primitives";
import ClassSessionActions from "@/components/studio/ClassSessionActions";
import { RosterHealthCard } from "@/components/RosterHealthCard";
import WelcomeCarousel from "@/components/WelcomeCarousel";
import AccountMenu from "@/components/AccountMenu";

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const canAddStudents = accountFeatureEnabled(user, "add_students");
  const navigate = useNavigate();
  const location = useLocation();
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [recentLessons, setRecentLessons] = useState([]);
  const [plans, setPlans] = useState([]);
  const [todayOccurrences, setTodayOccurrences] = useState([]);
  // Class meetings and lessons that cannot open recording show their details.
  const [picked, setPicked] = useState(null);
  const [scheduleComplete, setScheduleComplete] = useState(true);
  const [businessFilter, setBusinessFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [scheduleTimezone, setScheduleTimezone] = useState("");
  const [scheduleLoaded, setScheduleLoaded] = useState(false);
  // The sessions after today, read only once today has nothing left to teach.
  const [nextRows, setNextRows] = useState(null);
  /* Every class they teach, with what each still asks of them (I11): the
     right-hand column when there are classes. Best-effort; the class list
     itself decides whether the column exists. */
  const [myClasses, setMyClasses] = useState(null);
  // Recordings the recorder could not send, kept in this browser (B6): one
  // line near the top, only when there are any.
  const [savedCount, setSavedCount] = useState(0);
  useEffect(() => {
    let on = true;
    uploadQueue.listForAccount(user?.user_id)
      .then((rows) => { if (on) setSavedCount(rows.length); })
      .catch(() => {});
    return () => { on = false; };
  }, [user?.user_id]);

  // The welcome opens over the dashboard on two signals: arriving from the last
  // onboarding step (router state), or `?welcome=1`, which is the stable way
  // back to it — a welcome you can only ever see once is one most people miss.
  //
  // Both are CLEARED the moment they're read, so a refresh, a Back, or a
  // bookmark of the page underneath doesn't reopen it. That is the whole
  // "shown once" mechanism: nothing is persisted, because there is nothing to
  // persist once the trigger is gone.
  useEffect(() => {
    const asked = location.state?.welcome === true || new URLSearchParams(location.search).get("welcome") === "1";
    if (!asked) return;
    setWelcomeOpen(true);
    navigate("/dashboard", { replace: true, state: null });
  }, [location.key, location.search, location.state, navigate]);

  useEffect(() => {
    const controller = new AbortController();
    const fetchDashboardData = async () => {
      try {
        const [studentsRes, classesRes, lessonsRes] = await Promise.all([
          api.get("/students", { signal: controller.signal }),
          api.get("/classes", { signal: controller.signal }).catch(() => ({ data: [] })),
          api.get("/lessons?limit=5", { signal: controller.signal }),
        ]);
        setStudents(studentsRes.data || []);
        setClasses(classesRes.data || []);
        setRecentLessons(lessonsRes.data || []);
      } catch (error) {
        if (!controller.signal.aborted) console.error("Error fetching dashboard data:", error);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
      // What each class still asks of them — best-effort, like the plans.
      api.get("/marketplace/my-classes", { signal: controller.signal })
        .then((res) => { if (!controller.signal.aborted) setMyClasses(res.data || null); })
        .catch(() => {});
      // Saved lesson plans are best-effort — never block the dashboard.
      api.get("/repertoire/saved", { signal: controller.signal })
        .then((res) => { if (Array.isArray(res.data)) setPlans(res.data); })
        .catch(() => {});
      // Today's schedule is best-effort too.
      if (SCHEDULING_ENABLED) {
        teachingToday({ signal: controller.signal })
          .then((data) => { if (!controller.signal.aborted) { setTodayOccurrences(data?.occurrences || []); setScheduleComplete(data?.complete !== false); setScheduleTimezone(data?.timezone || ""); setScheduleLoaded(true); } })
          .catch(() => { if (!controller.signal.aborted) { setScheduleComplete(false); setScheduleLoaded(true); } });
      }
    };
    fetchDashboardData();
    return () => controller.abort();
  }, []);

  const businesses = useMemo(() => [...new Map(todayOccurrences.filter((row) => row.org_id)
    .map((row) => [row.org_id, row.org_name || row.business_label || "Organization"])).entries()], [todayOccurrences]);
  const visibleToday = todayOccurrences.filter((row) => inBusiness(row, businessFilter));
  // With nothing left to teach today, the card names the next lesson or
  // class on the calendar, under the same filter (mobile Home does the same).
  const todayIso = isoDate(new Date());
  const nowMs = Date.now();
  const wantsNext = SCHEDULING_ENABLED && scheduleLoaded && scheduleComplete && !anyStillAhead(visibleToday, nowMs);
  const nextOcc = wantsNext && nextRows ? nextSession(nextRows.filter((row) => inBusiness(row, businessFilter)), nowMs, todayIso) : null;
  useEffect(() => {
    if (!wantsNext || nextRows !== null) return undefined;
    const controller = new AbortController();
    teachingNext({ signal: controller.signal })
      .then((data) => { if (!controller.signal.aborted) setNextRows(data?.occurrences || []); })
      .catch(() => { if (!controller.signal.aborted) setNextRows([]); });
    return () => controller.abort();
  }, [wantsNext, nextRows]);

  const studentNameById = useMemo(() => {
    const map = new Map();
    for (const s of students) map.set(s.student_id, `${s.first_name || ""} ${s.last_name || ""}`.trim());
    return map;
  }, [students]);

  const classNameById = useMemo(() => {
    const map = new Map();
    for (const c of classes) map.set(c.class_id, c.name);
    return map;
  }, [classes]);

  const labelFor = (lesson) =>
    lesson.kind === "class"
      ? (classNameById.get(lesson.class_id) || "Class lesson")
      : (studentNameById.get(lesson.student_id) || "");

  const hrefFor = (lesson) =>
    lesson.kind === "class" ? `/lesson/${lesson.lesson_id}?kind=class` : `/lesson/${lesson.lesson_id}`;

  /* The latest four, read oldest first like every lesson list. */
  const recentShown = useMemo(() => sortChronologically(recentLessons, recordedAt).slice(-4), [recentLessons]);

  /* The classes column's rows: the summary when it answered, else the bare
     class list, so a class is never missing from Home because a count was. */
  const classRows = useMemo(() => {
    const summary = new Map((myClasses?.classes || []).map((c) => [c.class_id, c]));
    // A class the org archived (O5) is off Home: nothing of it is ahead.
    return classes.filter((c) => !c.archived_at && !summary.get(c.class_id)?.archived).map((c) => ({
      class_id: c.class_id,
      name: c.name,
      member_count: summary.get(c.class_id)?.member_count ?? (c.member_ids || []).length,
      next_session_at: summary.get(c.class_id)?.next_session_at ?? null,
      notes_missing: summary.get(c.class_id)?.notes_missing ?? null,
      program_id: summary.get(c.class_id)?.program_id ?? c.program_id ?? null,
    }));
  }, [classes, myClasses]);
  const classHref = (c) => (c.program_id ? programClassPath(user, c.program_id, c.class_id) : `/classes/${c.class_id}/sessions`);
  const nextLabel = (iso) => {
    if (!iso) return "No upcoming session";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "No upcoming session" : `Next ${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}`;
  };

  const greeting = firstName(user) || "there";

  return (
    <div className="mx-auto max-w-[1180px] px-6 pb-32 pt-11 md:px-12" data-testid="dashboard">
      <WelcomeCarousel open={welcomeOpen} onOpenChange={setWelcomeOpen} />

      {/* Greeting */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <Eyebrow>Welcome back</Eyebrow>
          <h1 className="mt-1.5 font-serif text-[44px] font-normal leading-none tracking-[-0.02em] text-[var(--text-primary)]">
            {greeting}
          </h1>
        </div>
        {/* Avatar + first name + chevron: minutes left, the plan, and the
            way to Settings, without leaving the page. */}
        <AccountMenu user={user} />
      </div>

      {savedCount > 0 && (
        <Card padding={16} className="mb-5" data-testid="saved-recordings-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-sans text-[14px] text-[var(--text-secondary)]">
              <span className="font-semibold text-[var(--accent-yellow-text)]">{savedCountLabel(savedCount)}</span>{" "}
              — kept in this browser until they’re sent.
            </p>
            <button
              type="button"
              onClick={() => navigate("/recordings/saved")}
              className="font-sans text-[14px] font-medium text-[var(--accent-yellow-text)] underline-offset-4 hover:underline"
              data-testid="open-saved-recordings"
            >
              Open
            </button>
          </div>
        </Card>
      )}

      {/* Who has gone quiet (F3) — the one thing an instructor cannot see
          any other way, so it sits above the day's logistics. */}
      <div className="mb-5">
        <RosterHealthCard />
      </div>

      {/* Today's schedule (Spec 04 Phase 1) */}
      {SCHEDULING_ENABLED && (
        <div className="mb-5">
          <SectionHeader action={SCHEDULER_VIEW_ENABLED ? "Manage scheduling →" : "Schedule →"}
            onAction={() => navigate(SCHEDULER_VIEW_ENABLED ? "/settings#scheduling" : "/schedule")} actionTestId="view-schedule">
            Today
          </SectionHeader>
          <Card padding={6} data-testid="today-card">
            {scheduleTimezone && <p className="px-4 pt-3 text-xs text-[var(--text-secondary)]">Times in {scheduleTimezone}</p>}
            {SCHEDULER_VIEW_ENABLED && <div className="px-4 pt-3">
              <label className="text-xs text-[var(--text-secondary)]">Show lessons
                <select aria-label="Show lessons by business" value={businessFilter} onChange={(event) => setBusinessFilter(event.target.value)}
                  className="ml-3 rounded-lg border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-3 py-2 text-[var(--text-primary)]">
                  <option value="all">All</option><option value="personal">Personal</option>
                  {businesses.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
              </label>
            </div>}
            {SCHEDULER_VIEW_ENABLED && !scheduleComplete && <p role="status" className="px-4 py-3 text-sm text-[var(--text-secondary)]">
              Some calendars are unavailable. This view may be incomplete.
            </p>}
            {visibleToday.length === 0 ? (
              <div className={`px-6 ${nextOcc ? "pt-7 pb-3" : "py-7"} text-center font-sans text-sm text-[var(--text-tertiary)]`}>
                {SCHEDULER_VIEW_ENABLED && !scheduleComplete ? "Refresh scheduling to see the full day." : "No lessons today"}
              </div>
            ) : (
              visibleToday.map((occ, i, arr) => {
                /* Covered by a substitute (docs/substitute-requests.md): still
                   on the day, greyed, and nothing to record or write. */
                const coveredElsewhere = Boolean(occ.covered_by && !occ.covered_by.mine);
                return (
                <div key={occurrenceKey(occ)} className={coveredElsewhere ? "opacity-60" : undefined}>
                <ListRow
                  data-testid={`today-occurrence-${occ.scheduler_appointment_id || occ.slot_id}`}
                  leading={
                    <div className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-[var(--studio-interactive)]">
                      <Icon name="calendar" size={17} className="text-[var(--accent-yellow-text)]" />
                    </div>
                  }
                  title={occ.display_name || "Lesson"}
                  wrapText={SCHEDULER_VIEW_ENABLED}
                  subtitle={coveredElsewhere ? `Covered by ${occ.covered_by.name || "a substitute (to be confirmed)"}` : <>{SCHEDULER_VIEW_ENABLED ? occ.context_label || "Personal" : occ.status === "moved" ? "Moved this week" : occ.class_id ? "Class lesson" : "Lesson"}<ClassTeachingDetails cls={classes.find(c => c.class_id === occ.class_id)} /></>}
                  meta={SCHEDULER_VIEW_ENABLED ? <span className="flex flex-col items-end gap-1 whitespace-nowrap sm:flex-row sm:items-center sm:gap-3">
                    <span>{formatTime12h(occ.time_local)}</span>
                    {occ.teaching_confirmed && <span className="text-xs text-[var(--text-secondary)]">Recorded</span>}
                  </span> : formatTime12h(occ.time_local)}
                  trailing={coveredElsewhere ? <span className="text-xs text-[var(--text-tertiary)]">Substitute</span>
                    : occ.covered_by?.mine ? <span className="text-xs text-[var(--accent-yellow-text)]">Covering</span>
                    : occ.teaching_confirmed ? (SCHEDULER_VIEW_ENABLED ? null : <span className="text-xs text-[var(--text-secondary)]">Recorded</span>)
                    : occ.lesson_id ? <StatusPill kind="Sent" /> : null}
                  borderBottom={i < arr.length - 1}
                  /* Unavailable or unmapped lessons need details and an
                     explanation, not the recorder's Settings fallback. */
                  onPress={coveredElsewhere ? undefined : occ.class_id || occ.recordable === false || !occ.student_id
                    ? () => setPicked(occ) : () => navigate(recordHrefForOccurrence(occ))}
                  onRecord={coveredElsewhere || occ.lesson_id || occ.teaching_confirmed || (occ.recordable === false && !occ.attendance_reviewable) ? undefined : () => navigate(recordHrefForOccurrence(occ))}
                  recordLabel={`Record ${occ.display_name || "lesson"}`}
                />
                </div>
                );
              })
            )}
            {nextOcc && (
              <div className={visibleToday.length > 0 ? "mt-2 border-t border-[var(--studio-divider)] pt-3" : undefined} data-testid="today-next">
                <Eyebrow className="px-4 pb-1">{nextHeading(nextOcc)}</Eyebrow>
                <ListRow
                  data-testid="today-next-row"
                  leading={
                    <div className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-[var(--studio-interactive)]">
                      <Icon name="calendar" size={17} className="text-[var(--text-secondary)]" />
                    </div>
                  }
                  title={nextOcc.display_name || (nextOcc.class_id ? "Class" : "Lesson")}
                  wrapText={SCHEDULER_VIEW_ENABLED}
                  subtitle={SCHEDULER_VIEW_ENABLED ? nextOcc.context_label || "Personal" : nextOcc.class_id ? "Class lesson" : "Lesson"}
                  meta={<span className="whitespace-nowrap">{tomorrowWord(nextOcc, todayIso) || shortDateLabel(nextOcc.effective_date || nextOcc.date)} · {formatTime12h(nextOcc.time_local)}</span>}
                  onPress={() => setPicked(nextOcc)}
                />
              </div>
            )}
          </Card>
        </div>
      )}

      <ClassSessionActions occurrence={picked} onOpenChange={(open) => !open && setPicked(null)} />

      {/* Quick-glance stats */}
      <div className="mb-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <StatCard
          value={students.length}
          label="Students"
          sub="On your roster"
          onPress={() => navigate("/students")}
          action={canAddStudents ? "Add student" : null}
          onAction={() => navigate("/students?add=1")}
          actionTestId="add-student-pill"
          data-testid="quick-action-students"
        />
        <StatCard
          value={recentLessons.length}
          label="Lessons recorded"
          sub="Recent activity"
          onPress={() => navigate("/all-lessons")}
        />
      </div>

      {/* Recent students · Recent lessons */}
      <div className="mt-3 grid grid-cols-1 gap-7 lg:grid-cols-2">
        <div>
          <SectionHeader action="View all →" onAction={() => navigate("/students")} actionTestId="view-all-students">Recent students</SectionHeader>
          <Card padding={6}>
            {loading ? (
              <SkeletonRows />
            ) : students.length === 0 ? (
              <EmptyState
                icon="users"
                text="No students yet"
                cta={canAddStudents ? "Add student" : null}
                onCta={() => navigate("/students")}
              />
            ) : (
              students.slice(0, 4).map((s, i, arr) => {
                const name = `${s.first_name || ""} ${s.last_name || ""}`.trim();
                return (
                  <ListRow
                    key={s.student_id}
                    data-testid={`student-card-${s.student_id}`}
                    leading={<Avatar name={name} size={42} accent />}
                    title={name}
                    subtitle={(s.instruments || []).join(" · ") || "No instrument"}
                    trailing={<ConsentBadge status={s.consent_status} programRoster={isProgramRosterStudent(s)} />}
                    borderBottom={i < Math.min(arr.length, 4) - 1}
                    onPress={() => navigate(`/students/${s.student_id}`)}
                    onRecord={() => navigate(`/hans/${s.student_id}`)}
                    recordLabel={`Record with ${name}`}
                  />
                );
              })
            )}
          </Card>
        </div>

        {/* The right-hand column is the instructor's classes when they have
            any (I11): name, next session, students, notes outstanding, each
            opening the class under its program. Without classes it stays
            Recent lessons, exactly as before. */}
        {!loading && classes.length > 0 ? (
        <div data-testid="my-classes">
          <SectionHeader action="View all →" onAction={() => navigate("/students?tab=classes")} actionTestId="view-all-classes">My classes</SectionHeader>
          <Card padding={6}>
            {classRows.slice(0, 4).map((c, i, arr) => (
              <ListRow
                key={c.class_id}
                data-testid={`class-row-${c.class_id}`}
                leading={
                  <div className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-[var(--studio-interactive)]">
                    <Icon name="users" size={17} className="text-[var(--accent-yellow-text)]" />
                  </div>
                }
                title={c.name}
                subtitle={`${c.member_count} student${c.member_count === 1 ? "" : "s"} · ${nextLabel(c.next_session_at)}`}
                trailing={
                  c.notes_missing > 0 ? (
                    <Tag accent>{c.notes_missing} to write</Tag>
                  ) : c.notes_missing === 0 && myClasses?.complete ? (
                    <Icon name="check" size={17} className="text-[var(--state-success)]" />
                  ) : null
                }
                borderBottom={i < Math.min(arr.length, 4) - 1}
                onPress={() => navigate(classHref(c))}
              />
            ))}
          </Card>
        </div>
        ) : (
        <div>
          <SectionHeader action="View all →" onAction={() => navigate("/all-lessons")}>Recent lessons</SectionHeader>
          <Card padding={6}>
            {loading ? (
              <SkeletonRows />
            ) : recentLessons.length === 0 ? (
              <EmptyState icon="mic" text="No lessons yet" cta="Record lesson" onCta={() => navigate("/hans")} />
            ) : (
              recentShown.map((l, i, arr) => {
                const isClass = l.kind === "class";
                const day = lessonDate(l)?.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                return (
                  <ListRow
                    key={l.lesson_id}
                    data-testid={`lesson-card-${l.lesson_id}`}
                    leading={
                      <div className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-[var(--studio-interactive)]">
                        <Icon name={isClass ? "users" : "mic"} size={17} className="text-[var(--accent-yellow-text)]" />
                      </div>
                    }
                    title={l.title}
                    subtitle={<>{[labelFor(l), day].filter(Boolean).join(" · ")}{SCHEDULER_VIEW_ENABLED && <span className="ml-2"><ProgramChip lesson={l} programTitle={l.program_title} orgName={l.org_name} accent={l.org_accent} /></span>}</>}
                    meta={formatLessonDuration(l.duration_seconds)}
                    trailing={l.email_sent ? <StatusPill kind="Sent" /> : null}
                    borderBottom={i < arr.length - 1}
                    onPress={() => navigate(hrefFor(l))}
                  />
                );
              })
            )}
          </Card>
        </div>
        )}
      </div>

      {/* Saved lesson plans */}
      {plans.length > 0 && (
        <div className="mt-9">
          <SectionHeader action="View all →" onAction={() => navigate("/repertoire/saved")} actionTestId="view-saved-plans">Saved lesson plans</SectionHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {plans.slice(0, 3).map((p) => (
              <Card
                key={p.repertoire_id}
                hover
                padding={18}
                onPress={() => navigate("/repertoire", { state: { viewRepertoire: p } })}
                className="flex flex-col gap-3.5"
              >
                <div className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] bg-[var(--accent-yellow-dim)]">
                  <Icon name="book" size={16} className="text-[var(--accent-yellow-text)]" />
                </div>
                <div>
                  <div className="font-sans text-base font-medium text-[var(--text-primary)]">{p.title}</div>
                  <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">
                    {[p.type, p.student_name].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Quick tools */}
      <div className="mt-9">
        <SectionHeader>Quick tools</SectionHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[
            { icon: "metronome", name: "Metronome", sub: "120 BPM", tab: "metronome", testId: "quick-action-tools" },
            { icon: "tuner", name: "Tuner", sub: "A = 440 Hz", tab: "tuner" },
          ].map((tool) => (
            <Card
              key={tool.name}
              hover
              padding={20}
              onPress={() => navigate(`/tools?tab=${tool.tab}`)}
              data-testid={tool.testId}
              className="flex items-center gap-4"
            >
              <div className="flex h-[46px] w-[46px] items-center justify-center rounded-[12px] bg-[var(--studio-interactive)]">
                <Icon name={tool.icon} size={22} strokeWidth={1.6} className="text-[var(--accent-yellow-text)]" />
              </div>
              <div className="flex-1">
                <div className="font-sans text-base font-medium text-[var(--text-primary)]">{tool.name}</div>
                <div className="mt-0.5 font-mono text-[13px] text-[var(--text-tertiary)]">{tool.sub}</div>
              </div>
              <Icon name="chevronRight" size={18} className="text-[var(--text-tertiary)]" />
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

const SkeletonRows = () => (
  <div className="space-y-2 p-3">
    {[1, 2, 3].map((i) => (
      <div key={i} className="skeleton h-14 rounded-xl" />
    ))}
  </div>
);

const EmptyState = ({ icon, text, cta, onCta }) => (
  <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
    <Icon name={icon} size={34} className="mb-3 text-[var(--text-quaternary)]" />
    <p className="mb-4 font-sans text-sm text-[var(--text-tertiary)]">{text}</p>
    <button
      type="button"
      onClick={onCta}
      className="rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-2 font-sans text-sm text-[var(--text-primary)]"
    >
      {cta}
    </button>
  </div>
);

export default Dashboard;
